<template>
  <div
    v-if="template && graphic"
    class="playlist-layer graphic"
    :data-test="`graphic:${graphic.template_id}`"
    :class="{ 'editing': isEditing }"
    data-component-type="graphic"
    @click.stop="onClickTimelineItem"
  >
    <div class="layer-info">
      <button
        title="Rearrange"
        class="drag-handle tb-btn tb-btn-link sm low-em"
      >
        <span class="fas fa-grip-vertical" />
      </button>

      <div class="layer-title flex-grow ml-2">
        <img
          v-if="preview_url"
          class="layer-preview mr-2"
          :src="preview_url"
          alt=""
        >

        <span v-if="isSavedGraphic" title="Saved graphic" class="fas fa-bookmark mr-2 text-primary"></span>
        <div class="flex-grow no-wrap--ellipsis">
          <div class="title-text">
            {{ graphic.title }}
          </div>

          <div class="description-text">{{ template.title }}</div>
        </div>
      </div>

      <div class="layer-description capitalize ml-1 stretch-self">
        <shoppable-dropzone
          v-if="isShoppable"
          v-model="shoppableList"
          :graphic="graphic"
          :element="{
            id: this.shoppableId,
          }"
          @input="addShoppableProduct"
          @remove="removeShoppableProduct"
        />

        <!-- Graphics inside a group don't have individual durations -->
        <duration
          v-if="showDuration"
          v-model:duration="item.displayTime"
        />

        <!-- Graphics inside a group can be individually hidden -->
        <div v-if="grouped" class="ml-auto">
          <button
            class="tb-btn tb-btn-link sm low-em layer-visibility inline-flex min-w-[1.75rem] items-center justify-center p-1"
            data-test="graphics-group-visibility-toggle"
            @click.stop="toggleVisible"
          >
            <span
              class="fas inline-block w-4 text-center"
              :class="isVisible ? 'fa-eye text-[0.8125rem]' : 'fa-eye-slash text-[0.8125rem]'"
            />
          </button>
        </div>
      </div>

      <preview-live-switch @live-clicked="onLiveSwitchClick" v-if="!grouped" :item='item'></preview-live-switch>

      <dropdown position="left" class="more-actions ml-1">
        <template #trigger>
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <i class="fas fa-ellipsis-v"></i>
          </button>
        </template>

        <template #dropdown-body>
          <button
            v-if="!isSavedGraphic"
            class="dropdown-list-item"
            @click="duplicateItem"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Playlist
            </span>
          </button>

          <button
            v-if="!isSavedGraphic && grouped"
            class="dropdown-list-item"
            @click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Group
            </span>
          </button>

          <button
            class="dropdown-list-item"
            @click="openSaveGraphicModal"
          >
            <span class="dropdown-list-item-label">
              Save a Copy
            </span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            title="Merge with above graphic or group in the playlist"
            @click.stop="mergeUp"
          >
            <span class="dropdown-list-item-label">
              Merge Up
            </span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            title="Merge with below graphic or group in the playlist"
            @click.stop="mergeDown"
          >
            <span class="dropdown-list-item-label">
              Merge Down
            </span>
          </button>

          <hr>

          <button
            v-if="socialPosts.length"
            class="dropdown-list-item"
            @click="clearPosts"
          >
            <div class="dropdown-list-item-label warning">
              Clear Posts
            </div>
          </button>

          <button
            class="dropdown-list-item"
            @click="removeItem"
          >
            <div class="dropdown-list-item-label warning">
              Remove
            </div>
          </button>
        </template>
      </dropdown>
    </div>

    <social-post-dropzone
      ref="socialDropzone"
      v-if="isSocialGraphic || isSocialDisplayGraphic"
      v-model="socialPosts"
      :queued-post="socialFields && socialFields.queuedPost"
      :root-item="rootItem"
      :layout="item"
      :is-current-layout="isCurrentLayout"
      :event-source="queuedPostEventSource"
      :is-multi-post-display="isMultiPostDisplay"
      @clear-event-source="handleClearEventSource"
      @sort="updateOutputs"
      @post-clicked="onClickPost"
      @delete-post="onDeletePost"
      @delete-tagboard="onDeleteTagboard"
    />

    <modal v-if="newGraphicModalOpen" @close="newGraphicModalOpen = false">
      <template #header>
        <div class="title">Save copy to production</div>
      </template>

      <div class="tb-form-group">
        <label class="tb-label">Graphic title</label>
        <input
          v-model="newGraphicTitle"
          type="text"
          class="tb-input lg text-[13px]"
          @keyup.right.stop
          @keyup.left.stop
        >
      </div>

      <p>A copy of this graphic will be available in the "Saved graphics" section under "Graphics".</p>

      <template #footer>
        <button
          class="tb-btn tb-btn-contained medium-em"
          @click="newGraphicModalOpen = false"
        >
          Cancel
        </button>

        <button
          class="tb-btn tb-btn-contained high-em"
          @click="saveAsNewGraphic"
        >
          Save
        </button>
      </template>
    </modal>
  </div>

  <div v-else class="playlist-layer deleted" :class="{active: (isCurrentLayout || isCurrentSampleLayout) }">
    <div class="layer-info">
      <div class="deleted-message">This template <span v-if="item.title">({{item.title}})</span> has been deleted.</div>
      <button class="tb-btn tb-btn-text sm medium-em ml-1" title="Remove item from playlist" v-on:click.stop.prevent="removeItem"><i class="fas fa-minus-circle"></i></button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { get, isObject } from 'lodash-es';

import TimelineMixins from '../../../mixins/timeline-mixins';

import ConduitEventsService from '../../../services/conduitEventsService';

import modal from '../../modal/Modal.vue';
import duration from '../duration-input/DurationInput.vue';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';
import SocialPostDropzone from './SocialPostDropzone.vue';
import ShoppableDropzone from './ShoppableDropzone.vue';

export default {
  name: 'TimelineGraphicComponent',
  mixins: [TimelineMixins],

  data() {
    return {
      newGraphicModalOpen: false,
      newGraphicTitle: '',
      isVisible: true,
      loadingGraphic: false,
      queuedPostEventSource: null, // 'click' | 'element' | null
    };
  },

  watch: {
    'item.hidden': {
      handler(val) {
        this.isVisible = !val;
      },
      immediate: true,
    },
  },

  components: {
    modal,
    duration,
    PreviewLiveSwitch,
    SocialPostDropzone,
    ShoppableDropzone,
  },

  computed: {
    ...mapState({
      postLookupTable: (store) => store.posts.lookupTable,
      production: (store) => store.production.production,
    }),
    ...mapState('shoppable', ['productsLookup']),

    isCurrentLayout() {
      return this.$store.state.production.production.index === this.item.timeline_id;
    },

    isShoppable() {
      if (isObject(this.item?.meta?.elementCounts)) {
        return Object.keys(this.item?.meta?.elementCounts).some((key) => key === 'shoppable');
      }

      return false;
    },

    shoppableId() {
      return this.isShoppable ? this.item?.meta?.typeLookup?.shoppable?.[0] : null;
    },

    shoppableList: {
      get() {
        if (this.isShoppable) {
          const d = get(this.item, `fields.${this.shoppableId}.data.selectedProduct`, null);
          return (d && Object.keys(d).length) ? [d] : [];
        }

        return [];
      },

      set(v) {
        if (!this.item.fields) {
          this.item.fields = {};
        }

        this.item.fields[this.shoppableId] = {
          visible: true,
          data: {
            type: v.source,
            selectedProduct: {
              currency: v.currency,
              currentPrice: v.price,
              id: v.sourceId,
              image: v.imageUrl,
              name: v.name,
              shortUrl: v.shortUrl,
              url: v.affiliateUrl,
              type: v.source,
              rawData: v,
            },
          },
        };

        this.updateOutputs();
      },
    },

    socialPosts: {
      get() {
        return this.socialFields?.posts || [];
      },

      set(posts) {
        const id = this.socialElementId || this.socialDisplayElementId;

        // Make sure fields exist, then update the posts in the fields.
        if (!this.item.fields) {
          this.item.fields = {};
        }

        if (!this.item.fields[id]) {
          this.item.fields[id] = {};
        }

        this.item.fields[id].posts = posts;
      },
    },

    showDuration() {
      // Graphics in a group can't control their own duration
      if (this.grouped) {
        return false;
      }

      // Social graphics with individual posts don't have a duration.
      // The duration is decided by # of posts, cycle speed, video durations, etc.
      if ((this.isSocialGraphic || this.isSocialDisplayGraphic) && !this.hasTagboard) {
        return false;
      }

      return true;
    },

    /**
    * List of IDs pointing to social layers in the graphic template.
    */
    socialElementId() {
      const [id] = this.template?.meta?.typeLookup?.['social-data'] || [];
      return id;
    },

    /**
    * Graphic fields for the social element in the graphic, if there is one.
    */
    socialFields() {
      const id = this.socialElementId || this.socialDisplayElementId;
      return get(this.item, ['fields', id], null);
    },

    isSocialGraphic() {
      return !!this.socialElementId;
    },

    socialDisplayElementId() {
      const [id] = this.template?.meta?.typeLookup?.['social-display'] || [];
      return id;
    },

    isSocialDisplayGraphic() {
      return !!this.socialDisplayElementId;
    },

    hasTagboard() {
      if (!this.isSocialGraphic && !this.isSocialDisplayGraphic) {
        return false;
      }

      // Grab fields for social layer
      // and check if there's a tagboard in there somewhere
      const [post] = this.socialFields?.posts || [];
      return post?.type === 'tagboard';
    },

    socialDisplayElement() {
      if (!this.isSocialDisplayGraphic) return null;

      const findElement = (elements) => {
        for (const el of elements) { // eslint-disable-line no-restricted-syntax
          if (el.id === this.socialDisplayElementId) return el;
          if (el.elements) {
            const found = findElement(el.elements);
            if (found) return found;
          }
        }

        return null;
      };

      return findElement(this.template?.elements || []);
    },

    isMultiPostDisplay() {
      if (!this.isSocialDisplayGraphic || !this.socialDisplayElement) {
        return false;
      }

      // Check the layout type from element settings or fields
      const layout = this.item.fields?.[this.socialDisplayElementId]?.layout
        || this.socialDisplayElement?.layout;

      return ['grid', 'waterfall', 'carousel', 'mural'].includes(layout);
    },

    timelineObject() {
      return this.production.timeline.find((i) => i.timeline_id === this.item.timeline_id);
    },

    timelineIndex() {
      return this.production.timeline.findIndex((i) => i.timeline_id === this.item.timeline_id);
    },

    canMergeUp() {
      // Can merge graphic into group if next element is graphic
      return ['graphic', 'graphics'].includes(this.prev?.layout) && !this.grouped;
    },

    canMergeDown() {
      // Can merge graphic into group if next element is graphic
      return ['graphic', 'graphics'].includes(this.next?.layout) && !this.grouped;
    },

    template() {
      return this.$store.state.graphics.templatesLookupTable[this.item.template_id];
    },

    graphic() {
      return this.item;
    },

    isSavedGraphic() {
      return this.item && this.item._id;
    },

    preview_url() {
      const temp = this.$store.state.graphics.templatesLookupTable[this.item.template_id];
      return temp?.preview_url;
    },
  },
  methods: {
    ...mapActions([
      'updateTimeline',
      'updatePreviewTimeline',
    ]),

    ...mapActions('shoppable', [
      'addToproductsLookup',
    ]),

    /**
    * Trigger updates in live.
    */
    updateOutputs() {
      this.updateTimeline({ force: true });
      this.updatePreviewTimeline({ force: true });
    },

    /**
    * Set queuedPost on the graphic fields for the social element.
    * The queuedPost in the fields is mainly to control the default post to show when turned on.
    * If the graphic is already live, then switch to that post.
    */
    onClickPost(postId) {
      const post = this.socialFields.posts.find((p) => p.post_id === postId);
      if (!post) return;

      if (this.grouped) {
        this.$emit('queued-post');
      }

      const currentPostId = this.socialFields.queuedPost;
      const newPostId = post.timeline_id;
      const isAlreadyQueued = currentPostId === newPostId;

      // Set source of the queued post
      this.queuedPostEventSource = 'click';

      // Update the queued post (triggers child watcher)
      this.socialFields.queuedPost = isAlreadyQueued ? null : newPostId;
      this.updateOutputs();
    },

    handleClearEventSource() {
      this.queuedPostEventSource = null;
    },

    /**
    * When the user clicks the Live switch, this handles any necessary tracking when going live.
    * If the layout is being activated, we track the currently queued post.
    * This ensures the tracking event correctly reflects a user-initiated "take live" action.
    */
    onLiveSwitchClick() {
      const isActivating = !this.isCurrentLayout;
      if (isActivating) {
        this.queuedPostEventSource = 'element';
        const postId = this.socialFields?.queuedPost;
        if (postId && this.$refs.socialDropzone?.trackLivePost) {
          this.$refs.socialDropzone.trackLivePost(postId, 'on', 'element');
        }
      }
    },

    /**
    * Delete social post from graphic
    */
    onDeletePost(postId) {
      this.socialFields.posts = this.socialFields.posts.filter((post) => post.post_id !== postId);
      this.updateOutputs();
    },

    /**
    * Remove all social posts tied to the graphic
    */
    clearPosts() {
      this.socialFields.posts = [];
      this.updateOutputs();
    },

    /**
    * Delete tagboard from social graphic
    */
    onDeleteTagboard() {
      // Only supports one tagboard anyway, so just reset back to nothing
      this.socialFields.posts = [];
      this.updateOutputs();
    },

    duplicateItemInGroup() {
      this.$store.dispatch('duplicateTimelineItemInGroup', { item: this.item });
    },

    openSaveGraphicModal() {
      this.newGraphicTitle = this.item.title;
      this.newGraphicModalOpen = true;
    },

    toggleVisible() {
      this.item.hidden = !this.item.hidden;
      this.isVisible = !this.item.hidden;

      this.updateTimeline({ force: true });
      this.updatePreviewTimeline({ force: true });

      ConduitEventsService.track(
        'producer.toggled.graphics-visibility',
        {
          production_id: this.production._id,
          timeline_id: this.item.timeline_id,
          hidden: this.item.hidden,
        },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },

    mergeUp() {
      this.$store.dispatch('mergeGraphics', {
        i1: this.index - 1,
        i2: this.index,
      });
    },

    mergeDown() {
      this.$store.dispatch('mergeGraphics', {
        i1: this.index,
        i2: this.index + 1,
      });
    },

    async saveAsNewGraphic() {
      this.$store.dispatch('startLoader');

      try {
        await this.$store.dispatch('createGraphic', {
          title: this.newGraphicTitle,
          graphic: this.item,
        });

        this.successToast('Graphic saved successfully.');
      } catch (err) {
        this.errorToast('There was an error saving your graphic. Please try again.');
      } finally {
        this.newGraphicModalOpen = false;
        this.$store.dispatch('stopLoader');
      }
    },

    addShoppableProduct() {
      if (!this.isCurrentLayout) {
        this.updateOutputs();
      }
    },

    removeShoppableProduct() {
      if (!this.item.fields[this.shoppableId]) {
        this.item.fields[this.shoppableId] = {};
      }
      this.item.fields[this.shoppableId].data = {
        type: null,
        selectedProduct: {},
      };

      if (!this.isCurrentLayout) {
        this.updateOutputs();
      }
    },
  },
};
</script>
